import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { dashboardAPI, ordersAPI } from '../lib/api';
import { formatCurrency, formatShortDate, formatDate } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  LayoutDashboard,
  ShoppingBag,
  FileText,
  Download,
  ArrowRight,
  Loader2,
  TrendingUp,
  CheckCircle,
  Eye,
  BookOpen,
  Lock
} from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function Dashboard() {
  const { user, token } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [knowledgeAccess, setKnowledgeAccess] = useState({ checked: false, hasAccess: false });
  const [knowledgeContent, setKnowledgeContent] = useState([]);

  useEffect(() => {
    loadDashboard();
    checkKnowledgeAccess();
  }, []);

  const loadDashboard = async () => {
    try {
      const response = await dashboardAPI.get();
      setDashboardData(response.data);
    } catch (error) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const checkKnowledgeAccess = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/knowledge/access-check`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setKnowledgeAccess({ checked: true, hasAccess: data.has_access });
        
        // If user has access, load the latest knowledge content
        if (data.has_access) {
          loadKnowledgeContent();
        }
      }
    } catch (error) {
      console.error('Failed to check knowledge access:', error);
      setKnowledgeAccess({ checked: true, hasAccess: false });
    }
  };

  const loadKnowledgeContent = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/knowledge`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        // Get only the latest 3 items for dashboard preview
        setKnowledgeContent(data.slice(0, 3));
      }
    } catch (error) {
      console.error('Failed to load knowledge content:', error);
    }
  };

  const handleDownloadReceipt = (orderId) => {
    window.open(`${ordersAPI.getReceipt(orderId)}?token=${token}`, '_blank');
  };

  const handleDownloadAgreement = (orderId) => {
    window.open(`${ordersAPI.getAgreement(orderId)}?token=${token}`, '_blank');
  };

  const handleDownloadPaymentConfirmation = (orderId) => {
    window.open(`${API_BASE}/api/orders/${orderId}/payment-confirmation?token=${token}`, '_blank');
  };

  const getStatusDisplay = (status) => {
    if (status === 'confirmed') {
      return { label: 'Confirmed', className: 'bg-green-100 text-green-700' };
    }
    return { label: 'Awaiting review by AfriCycle Zambia', className: 'bg-[#E4D5B7] text-[#A0522D]' };
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="dashboard-loading">
        <Loader2 className="h-8 w-8 animate-spin text-[#056839]" />
      </div>
    );
  }

  const { stats, recent_orders } = dashboardData || {};

  return (
    <div className="min-h-screen bg-[#F9FAFB]" data-testid="dashboard-page">
      {/* Header */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-[#D2E4D6] flex items-center justify-center">
              <LayoutDashboard className="h-6 w-6 text-[#056839]" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[#056839]">DASHBOARD</h1>
              <p className="text-[#6B7280]">{user?.company_name}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
          <div className="dashboard-card p-6 animate-fade-in stagger-1" data-testid="stat-orders">
            <div className="flex items-center justify-between mb-4">
              <div className="w-10 h-10 rounded-lg bg-[#D2E4D6] flex items-center justify-center">
                <ShoppingBag className="h-5 w-5 text-[#056839]" />
              </div>
              <TrendingUp className="h-5 w-5 text-[#69A488]" />
            </div>
            <p className="text-2xl font-bold text-[#056839]">{stats?.total_orders || 0}</p>
            <p className="text-[#6B7280] text-sm">Total Orders</p>
          </div>

          <div className="dashboard-card p-6 animate-fade-in stagger-2" data-testid="stat-services">
            <div className="flex items-center justify-between mb-4">
              <div className="w-10 h-10 rounded-lg bg-[#D2E4D6] flex items-center justify-center">
                <CheckCircle className="h-5 w-5 text-[#056839]" />
              </div>
            </div>
            <p className="text-2xl font-bold text-[#056839]">{stats?.active_services || 0}</p>
            <p className="text-[#6B7280] text-sm">Active Services</p>
          </div>

          <div className="dashboard-card p-6 animate-fade-in stagger-3" data-testid="stat-spent">
            <div className="flex items-center justify-between mb-4">
              <div className="w-10 h-10 rounded-lg bg-[#D2E4D6] flex items-center justify-center">
                <FileText className="h-5 w-5 text-[#056839]" />
              </div>
            </div>
            <p className="text-2xl font-bold text-[#056839]">{formatCurrency(stats?.total_spent || 0)}</p>
            <p className="text-[#6B7280] text-sm">Total Spent</p>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-[#D2E4D6]/30 rounded-xl p-6 mb-8 animate-slide-up">
          <h2 className="text-lg font-semibold text-[#056839] mb-4">QUICK ACTIONS</h2>
          <div className="flex flex-wrap gap-4">
            <Link to="/services">
              <Button className="btn-primary" data-testid="browse-services-action">
                Browse Services
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/calculator">
              <Button variant="outline" className="btn-outline" data-testid="calculator-action">
                Fee Calculator
              </Button>
            </Link>
          </div>
        </div>

        {/* Recent Orders */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm animate-slide-up">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-[#056839]">RECENT ORDERS</h2>
          </div>
          
          {recent_orders && recent_orders.length > 0 ? (
            <div className="divide-y divide-gray-100">
              {recent_orders.map((order, index) => {
                const statusInfo = getStatusDisplay(order.status);
                return (
                  <div key={order.id} className="p-6 hover:bg-[#F9FAFB] transition-colors" data-testid={`order-${index}`}>
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2 flex-wrap">
                          <p className="font-semibold text-[#056839]">{order.order_number}</p>
                          <Badge className={`${statusInfo.className} hover:${statusInfo.className}`}>
                            {statusInfo.label}
                          </Badge>
                        </div>
                        <p className="text-[#3F3F3F]">
                          {order.items.map(item => item.service_name).join(', ')}
                        </p>
                        <p className="text-[#6B7280] text-sm mt-1">
                          {formatShortDate(order.created_at)}
                        </p>
                        
                        {/* Payment Confirmation Indicator */}
                        {order.payment_confirmation_file && (
                          <div className="mt-2 flex items-center gap-2 text-sm text-green-600">
                            <CheckCircle className="h-4 w-4" />
                            <span>Payment confirmation available</span>
                          </div>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-4">
                        <p className="text-xl font-bold text-[#056839]">{formatCurrency(order.total)}</p>
                        <div className="flex gap-2">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleDownloadReceipt(order.id)}
                            className="text-[#056839]"
                            title="Download Receipt"
                            data-testid={`download-receipt-${index}`}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleDownloadAgreement(order.id)}
                            className="text-[#056839]"
                            title="Download Agreement"
                            data-testid={`download-agreement-${index}`}
                          >
                            <FileText className="h-4 w-4" />
                          </Button>
                          {order.payment_confirmation_file && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleDownloadPaymentConfirmation(order.id)}
                              className="text-green-600"
                              title="View Payment Confirmation"
                              data-testid={`view-payment-${index}`}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="p-12 text-center">
              <ShoppingBag className="h-12 w-12 text-[#D2E4D6] mx-auto mb-4" />
              <p className="text-[#6B7280] mb-4">No orders yet</p>
              <Link to="/services">
                <Button className="btn-primary" data-testid="first-order-btn">
                  Make Your First Order
                </Button>
              </Link>
            </div>
          )}
        </div>

        {/* Knowledge Hub Section */}
        {knowledgeAccess.checked && (
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm animate-slide-up mt-8" data-testid="knowledge-section">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#D2E4D6] flex items-center justify-center">
                  <BookOpen className="h-5 w-5 text-[#056839]" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-[#056839]">KNOWLEDGE HUB</h2>
                  <p className="text-sm text-[#6B7280]">Latest updates and resources</p>
                </div>
              </div>
            </div>
            
            {knowledgeAccess.hasAccess ? (
              /* User has access - show content */
              <div data-testid="knowledge-content">
                {knowledgeContent.length > 0 ? (
                  <>
                    <div className="divide-y divide-gray-100">
                      {knowledgeContent.map((item, index) => (
                        <Link 
                          key={item.id} 
                          to="/knowledge"
                          className="block p-4 hover:bg-[#F9FAFB] transition-colors"
                          data-testid={`knowledge-item-${index}`}
                        >
                          <div className="flex items-start gap-3">
                            <div className="flex-1">
                              <p className="font-medium text-[#056839] mb-1">{item.title}</p>
                              <p className="text-sm text-[#6B7280] line-clamp-2">{item.summary}</p>
                              <p className="text-xs text-[#9CA3AF] mt-2">
                                {formatDate(item.publish_date)}
                              </p>
                            </div>
                            <ArrowRight className="h-5 w-5 text-[#69A488] flex-shrink-0 mt-1" />
                          </div>
                        </Link>
                      ))}
                    </div>
                    <div className="p-4 border-t border-gray-100">
                      <Link to="/knowledge">
                        <Button variant="outline" className="w-full btn-outline" data-testid="view-all-knowledge-btn">
                          View All Updates
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </Link>
                    </div>
                  </>
                ) : (
                  <div className="p-8 text-center">
                    <BookOpen className="h-10 w-10 text-[#D2E4D6] mx-auto mb-3" />
                    <p className="text-[#6B7280]">No updates available yet</p>
                    <p className="text-sm text-[#9CA3AF] mt-1">Check back soon for the latest EPR news and resources</p>
                  </div>
                )}
              </div>
            ) : (
              /* User doesn't have access - show message card */
              <div className="p-8 text-center" data-testid="knowledge-access-denied">
                <div className="w-16 h-16 rounded-full bg-[#E4D5B7]/30 flex items-center justify-center mx-auto mb-4">
                  <Lock className="h-8 w-8 text-[#A0522D]" />
                </div>
                <h3 className="text-lg font-semibold text-[#056839] mb-2">Access to Knowledge Hub</h3>
                <p className="text-[#6B7280] mb-6 max-w-md mx-auto">
                  Access to industry updates and resources is available to members with an active compliance package.
                </p>
                <Link to="/services">
                  <Button className="btn-primary" data-testid="view-compliance-packages-btn">
                    View Compliance Packages
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
